import { Box, useMediaQuery } from "@mui/material";
import { ReactNode } from "react";
import { PageContent } from "@/components/common/PageLayout";
import { LAYOUT_SPACING } from "@/constants/layout";

/**
 * Two-column show-page body: a content column and a sticky action rail (#7581).
 *
 * The stop-loss claim page is the first record page with a rail, so the layout
 * lives here rather than inline, for the next page that wants one. Breakpoints
 * follow the design (`docs/design/stoploss-claim-show-redesign` in this repo, v6):
 *
 * - ≥ 900 px: content | rail (`railWidth`, default 340).
 * - 700–899 px: rail narrows to `railWidthNarrow` (default 280).
 * - < 700 px: the rail is not rendered; `collapsedRail`, when given, renders as
 *   a sticky bar under the header so the actions never leave the screen.
 *
 * The header, any banner and the sticky bar sit above the grid; only the two
 * columns are laid out here. The rail sticks to the top of the viewport while
 * the content scrolls.
 */

/** Below this width the rail collapses into the sticky bar. Not a theme breakpoint on purpose: the design chose 700. */
export const RAIL_COLLAPSE_QUERY = "(max-width: 699px)";
const RAIL_NARROW_QUERY = "(max-width: 899px)";

interface StyledShowWithRailProps {
  /** Page header, rendered full width above everything. */
  header: ReactNode;
  /** Optional full-width banner between the header and the body (a hold, a warning). */
  banner?: ReactNode;
  /** The rail. Rendered in the right column at ≥700 px, not at all below. */
  rail: ReactNode;
  /** Compact stand-in for the rail below 700 px, pinned under the header. */
  collapsedRail?: ReactNode;
  /** Content column (tabs). */
  children: ReactNode;
  /** Rail column width: a px number or any CSS length (e.g. `clamp(360px, 28%, 480px)`). */
  railWidth?: number | string;
  railWidthNarrow?: number | string;
  /** Offset for the sticky positions (an app bar above the page). */
  stickyTop?: number;
  /**
   * Which side the rail sits on. Right (default) for a thing you act on (the
   * claim page: actions in the rail); left for a person read in context (the
   * claimant page: position and notes in the rail). Part of each record type's
   * page signature (Feature #7659).
   */
  railSide?: "left" | "right";
}

export const useRailCollapsed = (): boolean => useMediaQuery(RAIL_COLLAPSE_QUERY);

export const StyledShowWithRail = ({
  header,
  banner,
  rail,
  collapsedRail,
  children,
  railWidth = 340,
  railWidthNarrow = 280,
  stickyTop = 0,
  railSide = "right",
}: StyledShowWithRailProps) => {
  const collapsed = useRailCollapsed();
  const css = (w: number | string) => (typeof w === "number" ? `${w}px` : w);
  const left = railSide === "left";
  const columns = (w: number | string) => (left ? `${css(w)} minmax(0, 1fr)` : `minmax(0, 1fr) ${css(w)}`);

  return (
    <Box data-testid="show-with-rail" data-rail-collapsed={collapsed ? "true" : "false"} data-rail-side={railSide}>
      {header}
      <PageContent>
        {banner ? <Box sx={{ mb: 2 }}>{banner}</Box> : null}
        {collapsed && collapsedRail ? (
          <Box
            data-testid="rail-collapsed-bar"
            sx={{
              position: "sticky",
              top: stickyTop,
              zIndex: (theme) => theme.zIndex.appBar - 1,
              mb: 2,
            }}
          >
            {collapsedRail}
          </Box>
        ) : null}
        <Box
          sx={{
            display: "grid",
            gap: LAYOUT_SPACING.COMPONENT,
            alignItems: "start",
            gridTemplateColumns: collapsed ? "minmax(0, 1fr)" : columns(railWidth),
            [`@media ${RAIL_NARROW_QUERY}`]: {
              gridTemplateColumns: collapsed ? "minmax(0, 1fr)" : columns(railWidthNarrow),
            },
            "& > *": { minWidth: 0 },
          }}
        >
          <Box sx={{ minWidth: 0, order: left ? 2 : 1 }}>{children}</Box>
          {!collapsed ? (
            <Box
              component="aside"
              data-testid="show-rail"
              sx={{
                position: "sticky",
                top: stickyTop + 16,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                order: left ? 1 : 2,
                // A rail taller than the viewport scrolls on its own; otherwise its
                // lower cards are out of reach until the content column ends.
                maxHeight: `calc(100vh - ${stickyTop + 32}px)`,
                overflowY: "auto",
              }}
            >
              {rail}
            </Box>
          ) : null}
        </Box>
      </PageContent>
    </Box>
  );
};
